import { useNavigate } from "@vixi/router";
import { useForm } from "react-hook-form";
import { HttpStatusCode } from "axios";
import { useTranslation } from "@vixi/localization";
import { RouteOptionsById, router, RouterIds } from "@vixi/router";
import { Button, ButtonProps, cn, FieldLabel, Img, TextInput, TextLink } from "@vixi/ui";
import { forwardRef } from "react";
import { LoginRequest } from "@vixi/types";
import { useLogin } from "@vixi/api";

// Constants
const routeId = "/_auth/login" satisfies RouterIds;
const Route = router.routesById[routeId];

export const LoginPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
};

export const AltLoginButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, ...props }, ref) => (
    <Button
      className={cn(
        "fs-22 fw-400 border-gray ar:fs-16 ar:fw-300 hover:bg-primary/25 w-76 justify-start gap-4.75 border bg-transparent py-[4.5px] ps-7 pe-4 text-start text-black",
        className,
      )}
      {...props}
      ref={ref}
    />
  ),
);

interface LoginFormValues extends Pick<LoginRequest, "email" | "password"> {}

function RouteComponent() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { register, handleSubmit } = useForm<LoginFormValues>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const { mutateAsync: login, isError, error, isPending } = useLogin();

  const onSubmit = async (data: LoginFormValues) => {
    await login(data, {
      onSuccess: () => {
        navigate({ to: "/create" });
      },
    });
  };

  return (
    <>
      <div className="mt-7.5 flex flex-col gap-y-6 justify-center ">
        <h1>{t("Auth.LogIn")}</h1>

        <form className="flex flex-col gap-y-5" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <FieldLabel size="medium">{t("Auth.Email")}</FieldLabel>
            <TextInput
              type="email"
              autoComplete="email"
              placeholder="Ex: mybook@domain.com"
              {...register("email")}
            />
          </div>

          <div>
            <FieldLabel size="medium">{t("Auth.Password")}</FieldLabel>
            <TextInput
              type="password"
              placeholder={t("Auth.Password")}
              autoComplete="current-password"
              {...register("password")}
            />
          </div>

          {isError && (
            <div className="w-81.75">
              <p className="text-destructive">
                {error.status === HttpStatusCode.Unauthorized
                  ? t("ErrorMessage.InvalidEmailOrPassword")
                  : (error.response?.data?.detail ?? t("ErrorMessage.SomethingWentWrong"))}
              </p>
            </div>
          )}

          <div className="mt-0.5 text-center">
            <Button disabled={isPending} type="submit" variant="contained" size="medium">
              {t("Action.LogIn")}
            </Button>
          </div>
        </form>

        <h4 className="fs-18">
          {`${t("Auth.DontHaveAnAccount")} `}
          <TextLink to="/signup" underline="none" className="fs-18">
            {t("Auth.SignUpNow")}
          </TextLink>
        </h4>
      </div>

    </>
  );
}
